import {
  BookOpen,
  Briefcase,
  CalendarDays,
  Factory,
  GraduationCap,
  Heart,
  Home,
  Instagram,
  Mail,

  Headphones,
  Mic,
  Wallet,
  Quote,
  Rocket,
  ScrollText,
  TrendingUp,
  Trophy,
  Users,

  type LucideIcon,
} from "lucide-react";



/**
 * Single source of truth for the keyword landing pages.
 * The hub widget, footer, About page deep-dive links and sitemap all read
 * from this list so they can never drift apart.
 */
export type KeywordHub = {
  /** Route path — matches the file in src/routes */
  to: string;
  icon: LucideIcon;
  /** Card / nav title */
  title: string;
  /** Short inline label used inside prose links */
  inlineLabel: string;
  /** Card description */
  desc: string;
  /** Sitemap priority */
  priority: string;
  /** Groups used to pull subsets into specific pages */
  tags: Array<"bio" | "business" | "content">;
};

export const keywordHubs: KeywordHub[] = [
  {
    to: "/raj-shamani-podcast",
    icon: Headphones,
    title: "Raj Shamani Podcast",
    inlineLabel: "the Raj Shamani podcast",
    desc: "What Figuring Out is, its biggest guests, release schedule and where to listen.",
    priority: "0.9",
    tags: ["content"],
  },
  {
    to: "/raj-shamani-books",
    icon: BookOpen,
    title: "Raj Shamani Books",
    inlineLabel: "Raj Shamani's books",
    desc: "Build Don't Talk — summary, core ideas, price in India and where to buy.",
    priority: "0.8",
    tags: ["content"],
  },
  {
    to: "/shamani-industries",
    icon: Factory,
    title: "Shamani Industries",
    inlineLabel: "Shamani Industries",
    desc: "The family manufacturing business, what it makes, and Raj's role in it.",
    priority: "0.8",
    tags: ["business"],
  },
  {
    to: "/raj-shamani-net-worth",
    icon: TrendingUp,
    title: "Raj Shamani Net Worth",
    inlineLabel: "net worth explained",
    desc: "What's actually disclosed, his five income streams, and why online figures disagree.",
    priority: "0.9",
    tags: ["bio", "business"],
  },
  {
    to: "/raj-shamani-age",
    icon: CalendarDays,
    title: "Raj Shamani Age",
    inlineLabel: "Raj Shamani's age & date of birth",
    desc: "Date of birth, current age, birthday, zodiac sign and what he built at each age.",
    priority: "0.9",
    tags: ["bio"],
  },
  {
    to: "/raj-shamani-family",
    icon: Users,
    title: "Family & Education",
    inlineLabel: "family, father & education",
    desc: "His Marwari family in Indore, his father's business and schooling at Daly College.",
    priority: "0.8",
    tags: ["bio"],
  },
  {
    to: "/raj-shamani-quotes",
    icon: Quote,
    title: "Raj Shamani Quotes",
    inlineLabel: "his best quotes",
    desc: "His most-shared lines on business, money and building — with the context behind each.",
    priority: "0.8",
    tags: ["content"],
  },
  {
    to: "/house-of-x",
    icon: Rocket,
    title: "House of X",
    inlineLabel: "House of X venture studio",
    desc: "The D2C venture studio he co-founded — what it builds, backs and how it operates.",
    priority: "0.8",
    tags: ["business"],
  },
  {
    to: "/raj-shamani-education",
    icon: GraduationCap,
    title: "Education & Qualification",
    inlineLabel: "his education and qualification",
    desc: "Daly College, his degree, why he skipped an MBA and how he actually learned business.",
    priority: "0.9",
    tags: ["bio"],
  },
  {
    to: "/raj-shamani-business",
    icon: Briefcase,
    title: "Raj Shamani Business",
    inlineLabel: "the businesses he runs",
    desc: "Every company he runs or co-owns — manufacturing, media, venture studio and investments.",
    priority: "0.9",
    tags: ["business"],
  },
  {
    to: "/raj-shamani-biography",
    icon: ScrollText,
    title: "Raj Shamani Biography",
    inlineLabel: "his full biography",
    desc: "Wiki-style life story — birthplace, schooling, first business, podcast, book and investments.",
    priority: "0.9",
    tags: ["bio"],
  },
  {
    to: "/figuring-out-podcast-guests",
    icon: Users,
    title: "Figuring Out Podcast Guests",
    inlineLabel: "the full guest list",
    desc: "Every Figuring Out guest — cricketers, founders, actors and investors — with episode links.",
    priority: "0.9",
    tags: ["content"],
  },
  {
    to: "/raj-shamani-wife",
    icon: Heart,
    title: "Raj Shamani Wife & Personal Life",
    inlineLabel: "his wife and personal life",
    desc: "Marital status, relationship rumours and what is actually confirmed about his private life.",
    priority: "0.8",
    tags: ["bio"],
  },
  {
    to: "/raj-shamani-instagram",
    icon: Instagram,
    title: "Raj Shamani Instagram",
    inlineLabel: "his official Instagram and socials",
    desc: "Every official account — Instagram, YouTube, LinkedIn and X — and how to spot fakes.",
    priority: "0.8",
    tags: ["content"],
  },
  {
    to: "/raj-shamani-income",
    icon: Wallet,
    title: "Raj Shamani Income",
    inlineLabel: "how he actually earns",
    desc: "Where the money comes from — manufacturing, media, brand deals, speaking and investments.",
    priority: "0.9",
    tags: ["bio", "business"],
  },
  {
    to: "/raj-shamani-speaker",
    icon: Mic,
    title: "Raj Shamani Speaker",
    inlineLabel: "booking him as a speaker",
    desc: "Keynote topics, past stages, audience fit and how to send a speaking enquiry.",
    priority: "0.8",
    tags: ["business"],
  },
  {
    to: "/raj-shamani-success-story",
    icon: Trophy,
    title: "Raj Shamani Success Story",
    inlineLabel: "his full success story",
    desc: "Indore to the UN stage — the stage-by-stage story and the lessons founders take from it.",
    priority: "0.9",
    tags: ["bio", "business"],
  },
  {
    to: "/raj-shamani-contact-details",
    icon: Mail,
    title: "Raj Shamani Contact Details",
    inlineLabel: "his official contact details",
    desc: "The official email, which channel to use for brands, speaking, press — and what gets a reply.",
    priority: "0.9",
    tags: ["business"],
  },
  {
    to: "/raj-shamani-lifestyle",
    icon: Home,
    title: "Raj Shamani Lifestyle",
    inlineLabel: "his lifestyle, house and cars",
    desc: "Where he lives, his work routine, and which house and car claims online are unverified.",
    priority: "0.8",
    tags: ["bio"],
  },
];





export function hubsByTag(tag: KeywordHub["tags"][number]) {
  return keywordHubs.filter((h) => h.tags.includes(tag));
}

export function getHub(to: string) {
  return keywordHubs.find((h) => h.to === to);
}
